import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, TimeoutError, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HeartbeatActions } from '@components/heartbeat/heartbeat.actions';
import { Logger } from '../../utils/logger';

/**
 * Http Error Interceptor.
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private readonly store = inject(Store);
  private readonly logger = Logger.getInstance('HttpErrorInterceptor');

  /**
   * @description Intercept HTTP requests and handle errors.
   * @template T - The type of the request.
   * @param {HttpRequest} req Request.
   * @param {HttpHandler} next Next.
   * @returns {Observable<HttpEvent<T>>}
   */
  intercept<T>(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<T>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        this.trace(req, error);

        // Log the error to the console
        this.logger.error(error);
        // Handle timeout error
        if (error instanceof TimeoutError) {
          // this.event.publish(EVENT.TOAST, `Request timeout`);
        }
        // Rethrow the error
        return throwError(() => error);
      })
    );
  }

  /**
   * @description Trace and dispatch HTTP error messages to the store.
   * @template T - The type of the request.
   * @param {HttpRequest} req The original HTTP request.
   * @param {HttpErrorResponse} error The HTTP error response.
   * @returns {void}
   */
  trace<T>(req: HttpRequest<T>, error: HttpErrorResponse): void {
    const messages: Array<string> = [];
    // Extract error message and dispatch to store
    if (error.error) {
      // Check if the error has a structured 'errors' array or a 'message' field
      if (error.error.errors) {
        // Handle array of errors
        if (Array.isArray(error.error.errors)) {
          // Iterate through each error and dispatch
          error.error.errors.forEach((err: any) => {
            if (typeof err === 'string') {
              messages.push(err);
            } else if (err.message) {
              messages.push(err.message);
            }
          });
        } else {
          messages.push(error.error.errors);
        }
      } else if (error.error.message) {
        messages.push(error.error.message);
      } else {
        messages.push(error.error);
      }
    } else if (error.message) {
      messages.push(error.message);
    }

    // Dispatch each message to the store
    for (const message of messages) {
      // Sanitize message by removing newlines and excessive whitespace
      // const message = msg.replace(/[\r\n\v\f\u0085\u2028\u2029]+/g, '').trim();
      this.store.dispatch(
        HeartbeatActions.sendMessage({
          message: `HTTP Error ${JSON.stringify({
            status: error.status,
            message,
            url: error.url ?? req.urlWithParams
          })}`
        })
      );
    }
  }
}
